<template>
    <div class="robots-container">
        <!-- 页面头部 -->
        <div class="page-header">
            <div class="header-content">
                <h1 class="page-title">
                    <i class="el-icon-cpu"></i>
                    机器人管理
                </h1>
                <p class="page-description">管理系统机器人，包括机器人信息查看、编辑、删除等操作</p>
            </div>
        </div>

        <!-- 搜索和操作区域 -->
        <div class="search-section">
            <el-card class="search-card" shadow="never">
                <div class="search-form">
                    <div class="search-row">
                        <div class="search-item">
                            <el-input
                                    v-model="searchForm.keyword"
                                    placeholder="搜索机器人名称或MQTT主题"
                                    prefix-icon="el-icon-search"
                                    clearable
                                    @keyup.enter.native="handleSearch"
                                    class="search-input"
                            />
                        </div>
                        <div class="search-item">
                            <el-input
                                    v-model="searchForm.username"
                                    placeholder="搜索用户名"
                                    prefix-icon="el-icon-user"
                                    clearable
                                    @keyup.enter.native="handleSearch"
                                    class="search-input"
                            />
                        </div>
                        <div class="search-item">
                            <el-input
                                    v-model="searchForm.user_id"
                                    placeholder="用户ID"
                                    clearable
                                    class="search-input"
                            />
                        </div>
                        <div class="search-item">
                            <el-input
                                    v-model="searchForm.robot_id"
                                    placeholder="机器人ID"
                                    clearable
                                    class="search-input"
                            />
                        </div>
                        <div class="search-item">
                            <el-input
                                    v-model="searchForm.chip_mac"
                                    placeholder="芯片MAC"
                                    clearable
                                    class="search-input"
                            />
                        </div>
                        <div class="search-item">
                            <el-select
                                    v-model="searchForm.device_type"
                                    placeholder="设备类型"
                                    clearable
                                    class="status-select"
                            >
                                <el-option label="全部设备类型" value=""/>
                                <el-option label="单开型" value="single"/>
                                <el-option label="双开型" value="double"/>
                                <el-option label="兼容型" value="compatible"/>
                            </el-select>
                        </div>
                        <div class="search-item">
                            <el-select
                                    v-model="searchForm.status"
                                    placeholder="当前状态"
                                    clearable
                                    class="status-select"
                            >
                                <el-option label="全部状态" value=""/>
                                <el-option label="关" value="off"/>
                                <el-option label="开" value="single_on"/>
                                <el-option label="左开" value="double_left"/>
                                <el-option label="右开" value="double_right"/>
                            </el-select>
                        </div>
                        <div class="search-item">
                            <el-select
                                    v-model="searchForm.online"
                                    placeholder="在线状态"
                                    clearable
                                    class="status-select"
                            >
                                <el-option label="全部" value=""/>
                                <el-option label="在线" value="true"/>
                                <el-option label="离线" value="false"/>
                            </el-select>
                        </div>
                        <div class="search-actions">
                            <el-button type="primary" icon="el-icon-search" @click="handleSearch">
                                搜索
                            </el-button>
                            <el-button icon="el-icon-refresh" @click="handleReset">
                                重置
                            </el-button>
                        </div>
                    </div>
                </div>
            </el-card>
        </div>

        <!-- 操作按钮区域 -->
        <div class="action-section">
            <el-card class="action-card" shadow="never">
                <div class="action-buttons">
                    <el-button
                            type="danger"
                            icon="el-icon-delete"
                            :disabled="selectedRobots.length === 0"
                            @click="handleBatchDelete"
                            class="batch-delete-btn"
                    >
                        批量删除 ({{ selectedRobots.length }})
                    </el-button>
                </div>
            </el-card>
        </div>

        <!-- 数据表格 -->
        <div class="table-section">
            <el-card class="table-card" shadow="never">
                <el-table
                        :data="robotList"
                        v-loading="loading"
                        @selection-change="handleSelectionChange"
                        stripe
                        class="robot-table"
                        :header-cell-style="{ background: '#f5f7fa', color: '#606266', textAlign: 'center' }"
                >
                    <el-table-column type="selection" width="55" align="center"/>
                    <el-table-column prop="id" label="ID" width="60" align="center"/>
                    <el-table-column prop="name" label="机器人名称" min-width="150" align="center">
                        <template slot-scope="scope">
                            <div class="robot-name-cell" style="display: flex; align-items: center; justify-content: center; gap: 8px;">
                                <i class="el-icon-cpu robot-icon"></i>
                                <span class="robot-name-text">{{ scope.row.name }}</span>
                            </div>
                        </template>
                    </el-table-column>
                    <el-table-column prop="user_id" label="用户ID" width="100" align="center"/>
                    <el-table-column prop="username" label="用户名" min-width="120" align="center" show-overflow-tooltip>
                        <template slot-scope="scope">
                            <div class="username-cell" style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                                <span>{{ scope.row.username }}</span>
                            </div>
                        </template>
                    </el-table-column>
                    <el-table-column prop="mqtt_topic" label="MQTT主题" min-width="100" align="center">
                        <template slot-scope="scope">
                            <div class="mqtt-cell">
                                <i class="el-icon-connection"></i>
                                <span>{{ scope.row.mqtt_topic }}</span>
                            </div>
                        </template>
                    </el-table-column>
                    <el-table-column prop="chip_mac" label="芯片MAC" min-width="150" align="center">
                        <template slot-scope="scope">
                            <div class="chip-cell" v-if="scope.row.chip_mac">
                                <i class="el-icon-postcard"></i>
                                <span>{{ scope.row.chip_mac }}</span>
                            </div>
                            <span v-else class="no-chip">-</span>
                        </template>
                    </el-table-column>
                    <el-table-column prop="device_type" label="设备类型" width="110" align="center">
                        <template slot-scope="scope">
                            <el-tag :type="getDeviceTypeColor(scope.row.device_type)" size="small">
                                {{ formatDeviceType(scope.row.device_type) }}
                            </el-tag>
                        </template>
                    </el-table-column>
                    <el-table-column prop="online" label="在线状态" width="100" align="center">
                        <template slot-scope="scope">
                            <el-tag :type="scope.row.online ? 'success' : 'info'" size="small">
                                {{ scope.row.online ? '在线' : '离线' }}
                            </el-tag>
                        </template>
                    </el-table-column>
                    <el-table-column prop="status" label="当前状态" width="110" align="center">
                        <template slot-scope="scope">
                            <el-tag :type="getStatusColor(scope.row.status)" size="small">
                                {{ formatStatus(scope.row.status) }}
                            </el-tag>
                        </template>
                    </el-table-column>
                    <el-table-column prop="firmware_ver" label="固件版本" width="100" align="center" />
                    <el-table-column label="操作" width="280" fixed="right">
                        <template slot-scope="scope">
                            <div class="action-buttons">
                                <el-button
                                        type="text"
                                        icon="el-icon-view"
                                        @click="handleView(scope.row)"
                                        class="action-btn view-btn"
                                >
                                    详情
                                </el-button>
                                <el-button
                                        type="text"
                                        icon="el-icon-edit"
                                        @click="handleEdit(scope.row)"
                                        class="action-btn edit-btn"
                                >
                                    编辑
                                </el-button>
                                <el-dropdown @command="(cmd) => handleMoreAction(cmd, scope.row)" class="more-dropdown">
                                    <el-button type="text" class="action-btn more-btn">
                                        更多<i class="el-icon-arrow-down el-icon--right"></i>
                                    </el-button>
                                    <el-dropdown-menu slot="dropdown">
                                        <el-dropdown-item command="schedules" icon="el-icon-time">定时任务
                                        </el-dropdown-item>
                                        <el-dropdown-item command="shares" icon="el-icon-share">分享信息
                                        </el-dropdown-item>
                                        <el-dropdown-item command="logs" icon="el-icon-document">执行日志
                                        </el-dropdown-item>
                                        <el-dropdown-item command="delete" icon="el-icon-delete">删除</el-dropdown-item>
                                    </el-dropdown-menu>
                                </el-dropdown>
                            </div>
                        </template>
                    </el-table-column>
                </el-table>

                <!-- 分页 -->
                <div class="pagination-wrapper">
                    <el-pagination
                            @size-change="handleSizeChange"
                            @current-change="handleCurrentChange"
                            :current-page="pagination.page"
                            :page-sizes="[10, 20, 50, 100]"
                            :page-size="pagination.pageSize"
                            layout="total, sizes, prev, pager, next, jumper"
                            :total="pagination.total"
                            class="pagination"
                    />
                </div>
            </el-card>
        </div>

        <!-- 机器人详情对话框 -->
        <el-dialog
                title="机器人详情"
                :visible.sync="detailDialogVisible"
                width="700px"
                class="detail-dialog"
        >
            <div v-if="currentRobot" class="robot-detail">
                <div class="detail-section">
                    <h3>基本信息</h3>
                    <div class="detail-grid">
                        <div class="detail-item">
                            <label>机器人ID：</label>
                            <span>{{ currentRobot.ID }}</span>
                        </div>
                        <div class="detail-item">
                            <label>机器人名称：</label>
                            <span>{{ currentRobot.name }}</span>
                        </div>
                        <div class="detail-item">
                            <label>用户ID：</label>
                            <span>{{ currentRobot.user_id }}</span>
                        </div>
                        <div class="detail-item">
                            <label>用户名：</label>
                            <span>{{ currentRobot.username }}</span>
                        </div>
                        <div class="detail-item">
                            <label>MQTT主题：</label>
                            <span>{{ currentRobot.mqtt_topic }}</span>
                        </div>
                        <div class="detail-item">
                            <label>芯片ID：</label>
                            <span>{{ currentRobot.chip_id || '-' }}</span>
                        </div>
                        <div class="detail-item">
                            <label>芯片MAC：</label>
                            <span>{{ currentRobot.chip_mac || '-' }}</span>
                        </div>
                        <div class="detail-item">
                            <label>设备类型：</label>
                            <el-tag :type="getDeviceTypeColor(currentRobot.device_type)" size="small">
                                {{ formatDeviceType(currentRobot.device_type) }}
                            </el-tag>
                        </div>
                        <div class="detail-item">
                            <label>当前状态：</label>
                            <el-tag :type="getStatusColor(currentRobot.status)" size="small">
                                {{ formatStatus(currentRobot.status) }}
                            </el-tag>
                        </div>
                        <div class="detail-item">
                            <label>在线状态：</label>
                            <el-tag :type="currentRobot.online ? 'success' : 'info'" size="small">
                                {{ currentRobot.online ? '在线' : '离线' }}
                            </el-tag>
                        </div>
                        <div class="detail-item">
                            <label>控制模式：</label>
                            <span>{{ currentRobot.mode === 'tap' ? '点击模式' : '切换模式' }}</span>
                        </div>
                        <div class="detail-item">
                            <label>静默模式：</label>
                            <span>{{ currentRobot.silence_mode ? '开启' : '关闭' }}</span>
                        </div>
                        <div class="detail-item">
                            <label>复位延迟：</label>
                            <span>{{ currentRobot.delay }}ms</span>
                        </div>
                        <div class="detail-item">
                            <label>最后在线：</label>
                            <span>{{ formatDate(currentRobot.last_online_at) || '-' }}</span>
                        </div>
                        <div class="detail-item">
                            <label>创建时间：</label>
                            <span>{{ formatDate(currentRobot.CreatedAt) }}</span>
                        </div>
                        <div class="detail-item">
                            <label>更新时间：</label>
                            <span>{{ formatDate(currentRobot.UpdatedAt) }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </el-dialog>

        <!-- 编辑机器人对话框 -->
        <el-dialog
                title="编辑机器人"
                :visible.sync="editDialogVisible"
                width="700px"
                class="form-dialog"
        >
            <el-form
                    :model="robotForm"
                    :rules="formRules"
                    ref="robotForm"
                    label-width="120px"
                    class="robot-form"
            >
                <el-form-item label="机器人名称" prop="name">
                    <el-input v-model="robotForm.name" placeholder="请输入机器人名称"/>
                </el-form-item>

                <el-form-item label="MQTT主题" prop="mqtt_topic">
                    <el-input v-model="robotForm.mqtt_topic" placeholder="请输入MQTT主题"/>
                </el-form-item>

                <el-form-item label="设备类型" prop="device_type">
                    <el-select v-model="robotForm.device_type" placeholder="请选择设备类型" style="width: 100%">
                        <el-option label="单开型" value="single"/>
                        <el-option label="双开型" value="double"/>
                        <el-option label="兼容旧设备" value="compatible"/>
                    </el-select>
                </el-form-item>

                <el-form-item label="当前状态" prop="status">
                    <el-select v-model="robotForm.status" placeholder="请选择状态" style="width: 100%">
                        <el-option label="关" value="off"/>
                        <el-option label="开" value="single_on"/>
                        <el-option label="左开" value="double_left"/>
                        <el-option label="右开" value="double_right"/>
                    </el-select>
                </el-form-item>

                <el-form-item label="控制模式" prop="mode">
                    <el-radio-group v-model="robotForm.mode">
                        <el-radio label="tap">点击模式</el-radio>
                        <el-radio label="toggle">切换模式</el-radio>
                    </el-radio-group>
                </el-form-item>

                <el-form-item label="静默模式" prop="silence_mode">
                    <el-switch v-model="robotForm.silence_mode"/>
                </el-form-item>

                <el-form-item label="延迟时间" prop="delay">
                    <el-input-number
                            v-model="robotForm.delay"
                            :min="0"
                            :max="10000"
                            :step="100"
                            style="width: 100%"
                    />
                    <span style="margin-left: 10px; color: #909399;">毫秒</span>
                </el-form-item>

                <el-divider content-position="left">角度设置</el-divider>

                <el-form-item label="按压角度" prop="angle_settings.single_angle">
                    <div class="angle-slider-wrapper">
                        <el-slider
                                v-model="robotForm.angle_settings.single_angle"
                                :min="0"
                                :max="180"
                                :show-tooltip="true"
                                :format-tooltip="formatAngleTooltip"
                        />
                        <span class="angle-value">{{ robotForm.angle_settings.single_angle }}°</span>
                    </div>
                </el-form-item>

                <el-form-item label="双开左角度" prop="angle_settings.double_left_angle">
                    <div class="angle-slider-wrapper">
                        <el-slider
                                v-model="robotForm.angle_settings.double_left_angle"
                                :min="0"
                                :max="90"
                                :show-tooltip="true"
                                :format-tooltip="formatAngleTooltip"
                        />
                        <span class="angle-value">{{ robotForm.angle_settings.double_left_angle }}°</span>
                    </div>
                </el-form-item>

                <el-form-item label="双开右角度" prop="angle_settings.double_right_angle">
                    <div class="angle-slider-wrapper">
                        <el-slider
                                v-model="robotForm.angle_settings.double_right_angle"
                                :min="0"
                                :max="90"
                                :show-tooltip="true"
                                :format-tooltip="formatAngleTooltip"
                        />
                        <span class="angle-value">{{ robotForm.angle_settings.double_right_angle }}°</span>
                    </div>
                </el-form-item>

                <el-form-item label="关闭角度" prop="angle_settings.off_angle">
                    <div class="angle-slider-wrapper">
                        <el-slider
                                v-model="robotForm.angle_settings.off_angle"
                                :min="0"
                                :max="180"
                                :show-tooltip="true"
                                :format-tooltip="formatAngleTooltip"
                        />
                        <span class="angle-value">{{ robotForm.angle_settings.off_angle }}°</span>
                    </div>
                </el-form-item>
            </el-form>
            <div slot="footer" class="dialog-footer">
                <el-button @click="editDialogVisible = false">取消</el-button>
                <el-button type="primary" @click="handleSubmit" :loading="submitLoading">
                    确定
                </el-button>
            </div>
        </el-dialog>

        <!-- 定时任务对话框 -->
        <el-dialog
                :title="`定时任务管理 - ${currentRobot ? currentRobot.name : ''}`"
                :visible.sync="schedulesDialogVisible"
                width="1100px"
                class="schedules-dialog"
        >
            <el-table
                    :data="schedulesList"
                    v-loading="schedulesLoading"
                    stripe
                    style="width: 100%"
            >
                <el-table-column prop="id" label="任务ID" width="80"/>
                <el-table-column prop="name" label="任务名称" min-width="120"/>
                <el-table-column prop="action" label="执行动作" width="100">
                    <template slot-scope="scope">
                        <el-tag :type="getStatusColor(scope.row.action)" size="small">
                            {{ formatStatus(scope.row.action) }}
                        </el-tag>
                    </template>
                </el-table-column>
                <el-table-column label="执行时间" min-width="200">
                    <template slot-scope="scope">
                        <div class="schedule-time">
                            {{ formatScheduleTime(scope.row) }}
                        </div>
                    </template>
                </el-table-column>
                <el-table-column prop="enabled" label="状态" width="90">
                    <template slot-scope="scope">
                        <el-tag :type="scope.row.enabled ? 'success' : 'info'" size="small">
                            {{ scope.row.enabled ? '已启用' : '已禁用' }}
                        </el-tag>
                    </template>
                </el-table-column>
                <el-table-column label="操作" width="240" fixed="right">
                    <template slot-scope="scope">
                        <el-button
                                type="text"
                                :icon="scope.row.enabled ? 'el-icon-video-pause' : 'el-icon-video-play'"
                                @click="handleToggleSchedule(scope.row)"
                                class="action-btn"
                        >
                            {{ scope.row.enabled ? '禁用' : '启用' }}
                        </el-button>
                        <el-button
                                type="text"
                                icon="el-icon-edit"
                                @click="handleEditSchedule(scope.row)"
                                class="action-btn edit-btn"
                        >
                            编辑
                        </el-button>
                        <el-button
                                type="text"
                                icon="el-icon-delete"
                                @click="handleDeleteSchedule(scope.row)"
                                class="action-btn delete-btn"
                        >
                            删除
                        </el-button>
                    </template>
                </el-table-column>
            </el-table>
        </el-dialog>

        <!-- 分享信息对话框 -->
        <el-dialog
                :title="`分享信息 - ${currentRobot ? currentRobot.name : ''}`"
                :visible.sync="sharesDialogVisible"
                width="800px"
                class="shares-dialog"
        >
            <el-table
                    :data="sharesList"
                    v-loading="sharesLoading"
                    stripe
                    style="width: 100%"
            >
                <el-table-column prop="ID" label="分享ID" width="80"/>
                <el-table-column prop="from_username" label="分享者" min-width="120"/>
                <el-table-column prop="to_username" label="被分享者" min-width="120"/>
                <el-table-column prop="role" label="权限角色" width="100">
                    <template slot-scope="scope">
                        <el-tag :type="getRoleColor(scope.row.role)" size="small">
                            {{ formatRole(scope.row.role) }}
                        </el-tag>
                    </template>
                </el-table-column>
                <el-table-column prop="is_active" label="状态" width="100">
                    <template slot-scope="scope">
                        <el-tag :type="scope.row.is_active ? 'success' : 'info'" size="small">
                            {{ scope.row.is_active ? '激活' : '未激活' }}
                        </el-tag>
                    </template>
                </el-table-column>
                <el-table-column prop="CreatedAt" label="创建时间" min-width="180">
                    <template slot-scope="scope">
                        {{ formatDate(scope.row.CreatedAt) }}
                    </template>
                </el-table-column>
            </el-table>
        </el-dialog>

        <!-- 日志对话框 -->
        <el-dialog
                :title="`执行日志 - ${currentRobot ? currentRobot.name : ''}`"
                :visible.sync="logsDialogVisible"
                width="1000px"
                class="logs-dialog"
        >
            <el-table
                    :data="logsList"
                    v-loading="logsLoading"
                    stripe
                    style="width: 100%"
            >
                <el-table-column prop="id" label="日志ID" width="80"/>
                <el-table-column prop="action" label="操作" width="100">
                    <template slot-scope="scope">
                        <el-tag :type="getStatusColor(scope.row.action)" size="small">
                            {{ formatStatus(scope.row.action) }}
                        </el-tag>
                    </template>
                </el-table-column>
                <el-table-column prop="result" label="结果" width="100">
                    <template slot-scope="scope">
                        <el-tag :type="getResultColor(scope.row.result)" size="small">
                            {{ formatResult(scope.row.result) }}
                        </el-tag>
                    </template>
                </el-table-column>
                <el-table-column prop="operator_name" label="操作者" min-width="120"/>
                <el-table-column prop="client_ip" label="客户端IP" min-width="130"/>
                <el-table-column prop="error_message" label="错误信息" min-width="200" show-overflow-tooltip/>
                <el-table-column prop="created_at" label="执行时间" min-width="180">
                    <template slot-scope="scope">
                        {{ formatDate(scope.row.created_at) }}
                    </template>
                </el-table-column>
            </el-table>

            <!-- 日志分页 -->
            <div class="pagination-wrapper">
                <el-pagination
                        @size-change="handleLogsSizeChange"
                        @current-change="handleLogsCurrentChange"
                        :current-page="logsPagination.page"
                        :page-sizes="[10, 20, 50]"
                        :page-size="logsPagination.pageSize"
                        layout="total, sizes, prev, pager, next"
                        :total="logsPagination.total"
                        class="pagination"
                />
            </div>
        </el-dialog>
    </div>
</template>

<script>
import {
    batchDeleteAdminRobots,
    deleteAdminRobot,
    getAdminRobotDetail,
    getAdminRobotList,
    getAdminRobotLogs,
    getAdminRobotSchedules,
    getAdminRobotShares,
    updateAdminRobot
} from '@/api/robot'
import {deleteScheduledTask, toggleScheduledTask} from '@/api/schedule'

export default {
    name: 'AdminRobots',
    data() {
        return {
            loading: false,
            robotList: [],
            selectedRobots: [],
            searchForm: {
                keyword: '',
                username: '',
                user_id: '',
                robot_id: '',
                device_type: '',
                status: '',
                online: '',
                chip_mac: ''
            },
            pagination: {
                page: 1,
                pageSize: 10,
                total: 0
            },
            detailDialogVisible: false,
            editDialogVisible: false,
            schedulesDialogVisible: false,
            sharesDialogVisible: false,
            logsDialogVisible: false,
            currentRobot: null,
            robotForm: {
                name: '',
                mqtt_topic: '',
                device_type: 'single',
                status: 'off',
                mode: 'tap',
                silence_mode: false,
                delay: 500,
                angle_settings: {
                    single_angle: 42,
                    double_left_angle: 42,
                    double_right_angle: 42,
                    off_angle: 90
                }
            },
            formRules: {
                name: [
                    {required: true, message: '请输入机器人名称', trigger: 'blur'},
                    {min: 1, max: 100, message: '名称长度在 1 到 100 个字符', trigger: 'blur'}
                ],
                mqtt_topic: [
                    {required: true, message: '请输入MQTT主题', trigger: 'blur'},
                    {min: 1, max: 255, message: 'MQTT主题长度在 1 到 255 个字符', trigger: 'blur'}
                ],
                device_type: [
                    {required: true, message: '请选择设备类型', trigger: 'change'}
                ],
                status: [
                    {required: true, message: '请选择状态', trigger: 'change'}
                ],
                mode: [
                    {required: true, message: '请选择控制模式', trigger: 'change'}
                ],
                delay: [
                    {required: true, message: '请输入复位延迟时间', trigger: 'blur'}
                ]
            },
            submitLoading: false,
            schedulesList: [],
            schedulesLoading: false,
            sharesList: [],
            sharesLoading: false,
            logsList: [],
            logsLoading: false,
            logsPagination: {
                page: 1,
                pageSize: 20,
                total: 0
            }
        }
    },
    created() {
        this.getRobotList()
    },
    methods: {
        // 获取机器人列表
        async getRobotList() {
            this.loading = true
            try {
                const params = {
                    page: this.pagination.page,
                    pageSize: this.pagination.pageSize,
                    keyword: this.searchForm.keyword,
                    username: this.searchForm.username,
                    user_id: this.searchForm.user_id,
                    robot_id: this.searchForm.robot_id,
                    device_type: this.searchForm.device_type,
                    status: this.searchForm.status,
                    online: this.searchForm.online,
                    chip_mac: this.searchForm.chip_mac
                }
                const response = await getAdminRobotList(params)
                this.robotList = response.data.list || []
                this.pagination.total = response.data.total
            } finally {
                this.loading = false
            }
        },

        // 搜索
        handleSearch() {
            this.pagination.page = 1
            this.getRobotList()
        },

        // 重置搜索
        handleReset() {
            this.searchForm = {
                keyword: '',
                username: '',
                user_id: '',
                robot_id: '',
                device_type: '',
                status: '',
                online: '',
                chip_mac: ''
            }
            this.pagination.page = 1
            this.getRobotList()
        },

        // 分页大小改变
        handleSizeChange(size) {
            this.pagination.pageSize = size
            this.pagination.page = 1
            this.getRobotList()
        },

        // 当前页改变
        handleCurrentChange(page) {
            this.pagination.page = page
            this.getRobotList()
        },

        // 选择改变
        handleSelectionChange(selection) {
            this.selectedRobots = selection
        },

        // 查看机器人详情
        async handleView(robot) {
            try {
                const response = await getAdminRobotDetail(robot.id)
                this.currentRobot = response.data
                this.detailDialogVisible = true
            } catch (error) {
                this.$message.error('获取机器人详情失败')
            }
        },

        // 编辑机器人
        async handleEdit(robot) {
            this.currentRobot = robot

            // 获取完整的机器人信息（包含角度设置）
            try {
                const response = await getAdminRobotDetail(robot.id)
                const robotDetail = response.data

                // 解析角度设置
                let angleSettings = {
                    single_angle: 42,
                    double_left_angle: 42,
                    double_right_angle: 42,
                    off_angle: 90
                }

                if (robotDetail.angle_settings) {
                    try {
                        angleSettings = JSON.parse(robotDetail.angle_settings)
                    } catch (e) {
                        console.error('解析角度设置失败:', e)
                    }
                }

                this.robotForm = {
                    name: robotDetail.name,
                    mqtt_topic: robotDetail.mqtt_topic,
                    device_type: robotDetail.device_type,
                    status: robotDetail.status,
                    mode: robotDetail.mode,
                    silence_mode: robotDetail.silence_mode,
                    delay: robotDetail.delay,
                    angle_settings: angleSettings
                }

                this.editDialogVisible = true
                this.$nextTick(() => {
                    this.$refs.robotForm.clearValidate()
                })
            } catch (error) {
                this.$message.error('获取机器人详情失败')
            }
        },

        // 提交表单
        async handleSubmit() {
            try {
                await this.$refs.robotForm.validate()
                this.submitLoading = true

                // 准备提交的数据
                const submitData = {
                    name: this.robotForm.name,
                    mqtt_topic: this.robotForm.mqtt_topic,
                    device_type: this.robotForm.device_type,
                    status: this.robotForm.status,
                    mode: this.robotForm.mode,
                    silence_mode: this.robotForm.silence_mode,
                    delay: this.robotForm.delay,
                    angle_settings: this.robotForm.angle_settings
                }

                await updateAdminRobot(this.currentRobot.id, submitData)
                this.$message.success('机器人更新成功')

                this.editDialogVisible = false
                this.getRobotList()
            } catch (error) {
                // 错误已由全局处理
            } finally {
                this.submitLoading = false
            }
        },

        // 格式化角度提示
        formatAngleTooltip(val) {
            return `${val}°`
        },

        // 删除机器人
        handleDelete(robot) {
            this.$confirm(`确定要删除机器人 "${robot.name}" 吗？删除后将同时删除该机器人的定时任务和分享记录！`, '提示', {
                confirmButtonText: '确定',
                cancelButtonText: '取消',
                type: 'warning'
            }).then(async () => {
                await deleteAdminRobot(robot.id)
                this.$message.success('删除成功')
                this.getRobotList()
            }).catch(() => {
            })
        },

        // 批量删除
        handleBatchDelete() {
            if (this.selectedRobots.length === 0) {
                this.$message.warning('请选择要删除的机器人')
                return
            }

            this.$confirm(`确定要删除选中的 ${this.selectedRobots.length} 个机器人吗？删除后将同时删除相关的定时任务和分享记录！`, '提示', {
                confirmButtonText: '确定',
                cancelButtonText: '取消',
                type: 'warning'
            }).then(async () => {
                const ids = this.selectedRobots.map(robot => robot.id)
                await batchDeleteAdminRobots(ids)
                this.$message.success('批量删除成功')
                this.getRobotList()
            }).catch(() => {
            })
        },

        // 处理"更多"菜单操作
        handleMoreAction(command, robot) {
            switch (command) {
                case 'schedules':
                    this.handleViewSchedules(robot)
                    break
                case 'shares':
                    this.handleViewShares(robot)
                    break
                case 'logs':
                    this.handleViewLogs(robot)
                    break
                case 'delete':
                    this.handleDelete(robot)
                    break
            }
        },

        // 查看定时任务
        async handleViewSchedules(robot) {
            this.currentRobot = robot
            this.schedulesDialogVisible = true
            this.schedulesLoading = true
            try {
                const response = await getAdminRobotSchedules(robot.id)
                this.schedulesList = response.data || []
            } finally {
                this.schedulesLoading = false
            }
        },

        // 切换定时任务启用状态
        async handleToggleSchedule(schedule) {
            try {
                await toggleScheduledTask(schedule.ID, !schedule.enabled)
                this.$message.success(schedule.enabled ? '已禁用定时任务' : '已启用定时任务')
                this.handleViewSchedules(this.currentRobot)
            } catch (error) {
                // 错误已由全局处理
            }
        },

        // 编辑定时任务
        handleEditSchedule() {
            this.$message.info('定时任务编辑功能需要在定时任务管理页面实现')
            // 这里可以跳转到定时任务编辑页面或打开编辑对话框
        },

        // 删除定时任务
        handleDeleteSchedule(schedule) {
            this.$confirm(`确定要删除定时任务 "${schedule.name}" 吗？`, '提示', {
                confirmButtonText: '确定',
                cancelButtonText: '取消',
                type: 'warning'
            }).then(async () => {
                await deleteScheduledTask(schedule.ID)
                this.$message.success('删除成功')
                this.handleViewSchedules(this.currentRobot)
            }).catch(() => {
            })
        },

        // 格式化定时任务执行时间
        formatScheduleTime(schedule) {
            // 如果是间隔执行类型
            if (schedule.interval_seconds && schedule.interval_seconds > 0) {
                const seconds = schedule.interval_seconds
                if (seconds < 60) {
                    return `每 ${seconds} 秒执行一次`
                } else if (seconds < 3600) {
                    const minutes = Math.floor(seconds / 60)
                    const remainSeconds = seconds % 60
                    return remainSeconds > 0
                        ? `每 ${minutes} 分 ${remainSeconds} 秒执行一次`
                        : `每 ${minutes} 分钟执行一次`
                } else {
                    const hours = Math.floor(seconds / 3600)
                    const minutes = Math.floor((seconds % 3600) / 60)
                    return minutes > 0
                        ? `每 ${hours} 小时 ${minutes} 分钟执行一次`
                        : `每 ${hours} 小时执行一次`
                }
            }

            // 格式化时分秒
            const hour = schedule.hour !== null && schedule.hour !== undefined ? schedule.hour : 0
            const minute = schedule.minute !== null && schedule.minute !== undefined ? schedule.minute : 0
            const second = schedule.second !== null && schedule.second !== undefined ? schedule.second : 0
            const timeStr = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')}`

            // 如果是每周类型且有weekdays
            if (schedule.type === 'weekly' && schedule.weekdays) {
                const weekdaysArr = schedule.weekdays.split(',').map(d => d.trim())
                const weekdayNames = {
                    '1': '周一', '2': '周二', '3': '周三', '4': '周四',
                    '5': '周五', '6': '周六', '7': '周日'
                }
                const weekdayStr = weekdaysArr.map(d => weekdayNames[d] || d).join('、')
                return `${weekdayStr} ${timeStr}`
            }

            if (schedule.type === 'workday') {
                return `法定工作日 ${timeStr}`
            }
            if (schedule.type === 'holiday') {
                return `法定节假日 ${timeStr}`
            }
            if (schedule.type === 'daily') {
                return `每天 ${timeStr}`
            }

            return timeStr
        },

        // 查看分享信息
        async handleViewShares(robot) {
            this.currentRobot = robot
            this.sharesDialogVisible = true
            this.sharesLoading = true
            try {
                const response = await getAdminRobotShares(robot.id)
                this.sharesList = response.data || []
            } finally {
                this.sharesLoading = false
            }
        },

        // 查看日志
        async handleViewLogs(robot) {
            this.currentRobot = robot
            this.logsDialogVisible = true
            this.logsPagination.page = 1
            this.loadRobotLogs()
        },

        // 加载机器人日志
        async loadRobotLogs() {
            this.logsLoading = true
            try {
                const params = {
                    page: this.logsPagination.page,
                    pageSize: this.logsPagination.pageSize
                }
                const response = await getAdminRobotLogs(this.currentRobot.id, params)
                this.logsList = response.data.list || []
                this.logsPagination.total = response.data.total
            } finally {
                this.logsLoading = false
            }
        },

        // 日志分页大小改变
        handleLogsSizeChange(size) {
            this.logsPagination.pageSize = size
            this.logsPagination.page = 1
            this.loadRobotLogs()
        },

        // 日志当前页改变
        handleLogsCurrentChange(page) {
            this.logsPagination.page = page
            this.loadRobotLogs()
        },

        // 格式化设备类型
        formatDeviceType(type) {
            const typeMap = {
                'single': '单开型',
                'double': '双开型',
                'compatible': '兼容型'
            }
            return typeMap[type] || type
        },

        // 获取设备类型颜色
        getDeviceTypeColor(type) {
            const colorMap = {
                'single': 'success',
                'double': 'warning',
                'compatible': 'primary'
            }
            return colorMap[type] || ''
        },

        // 格式化状态
        formatStatus(status) {
            const statusMap = {
                'off': '关',
                'single_on': '开',
                'double_left': '左开',
                'double_right': '右开'
            }
            return statusMap[status] || status
        },

        // 获取状态颜色
        getStatusColor(status) {
            const colorMap = {
                'off': 'info',
                'single_on': 'success',
                'double_left': 'warning',
                'double_right': 'danger'
            }
            return colorMap[status] || ''
        },

        // 格式化角色
        formatRole(role) {
            const roleMap = {
                'admin': '管理员',
                'operator': '操作员',
                'viewer': '查看者'
            }
            return roleMap[role] || role
        },

        // 获取角色颜色
        getRoleColor(role) {
            const colorMap = {
                'admin': 'danger',
                'operator': 'warning',
                'viewer': 'info'
            }
            return colorMap[role] || ''
        },

        // 格式化结果
        formatResult(result) {
            const resultMap = {
                'success': '成功',
                'failed': '失败',
                'timeout': '超时'
            }
            return resultMap[result] || result
        },

        // 获取结果颜色
        getResultColor(result) {
            const colorMap = {
                'success': 'success',
                'failed': 'danger',
                'timeout': 'warning'
            }
            return colorMap[result] || ''
        },

        // 格式化日期
        formatDate(dateString) {
            if (!dateString) return '-'
            const date = new Date(dateString)
            return date.toLocaleString('zh-CN', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
            })
        }
    }
}
</script>

<style scoped>
.robots-container {
    padding: 20px;
    background-color: #f5f7fa;
    min-height: 100vh;
}

/* 页面头部 */
.page-header {
    margin-bottom: 20px;
}

.header-content {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    padding: 30px;
    border-radius: 12px;
    color: white;
    box-shadow: 0 4px 20px rgba(102, 126, 234, 0.3);
}

.page-title {
    margin: 0 0 10px 0;
    font-size: 28px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 12px;
}

.page-description {
    margin: 0;
    opacity: 0.9;
    font-size: 16px;
}

/* 搜索区域 */
.search-section {
    margin-bottom: 20px;
}

.search-card {
    border-radius: 12px;
    border: none;
}

.search-form {
    padding: 10px 0;
}

.search-row {
    display: flex;
    gap: 16px;
    align-items: center;
    flex-wrap: wrap;
}

.search-item {
    flex: 1;
    min-width: 200px;
}

.search-input {
    width: 100%;
}

.status-select {
    width: 100%;
}

.search-actions {
    display: flex;
    gap: 12px;
}

/* 操作区域 */
.action-section {
    margin-bottom: 20px;
}

.action-card {
    border-radius: 12px;
    border: none;
}

.batch-delete-btn {
    border-radius: 8px;
    padding: 12px 24px;
    font-weight: 500;
}

/* 表格区域 */
.table-section {
    margin-bottom: 20px;
}

.table-card {
    border-radius: 12px;
    border: none;
    overflow: hidden;
}

.robot-table {
    width: 100%;
}

.robot-name-cell {
    display: flex;
    align-items: center;
    gap: 12px;
}

.robot-icon {
    color: #667eea;
    font-size: 18px;
}

.robot-name-text {
    font-weight: 500;
    color: #303133;
}

.username-cell {
    color: #606266;
}

.mqtt-cell {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #606266;
    font-family: monospace;
}

.chip-cell {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #606266;
    font-family: 'Courier New', monospace;
}

.no-chip {
    color: #c0c4cc;
    font-style: italic;
}

.action-buttons {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
}

.action-btn {
    padding: 4px 4px;
    border-radius: 4px;
    font-size: 14px;
    transition: all 0.3s;
}

.view-btn {
    color: #409eff;
}

.view-btn:hover {
    background-color: #ecf5ff;
    color: #337ecc;
}

.schedule-btn {
    color: #e6a23c;
}

.schedule-btn:hover {
    background-color: #fdf6ec;
    color: #b88230;
}

.share-btn {
    color: #909399;
}

.share-btn:hover {
    background-color: #f4f4f5;
    color: #73767a;
}

.edit-btn {
    color: #67c23a;
}

.edit-btn:hover {
    background-color: #f0f9ff;
    color: #529b2e;
}

.log-btn {
    color: #409eff;
}

.log-btn:hover {
    background-color: #ecf5ff;
    color: #337ecc;
}

.delete-btn {
    color: #f56c6c;
}

.delete-btn:hover {
    background-color: #fef0f0;
    color: #c45656;
}

.more-dropdown {
    display: inline-block;
    margin: 0 4px;
}

.more-btn {
    color: #909399;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 14px;
    transition: all 0.3s;
}

.more-btn:hover {
    background-color: #f4f4f5;
    color: #73767a;
}

.schedule-time {
    color: #606266;
    font-size: 14px;
    line-height: 1.5;
}

/* 角度滑块 */
.angle-slider-wrapper {
    display: flex;
    align-items: center;
    gap: 20px;
}

.angle-slider-wrapper .el-slider {
    flex: 1;
}

.angle-value {
    min-width: 50px;
    text-align: right;
    font-weight: 600;
    color: #667eea;
    font-size: 16px;
}

.robot-form .el-divider {
    margin: 30px 0 20px 0;
}

.robot-form .el-divider__text {
    font-weight: 600;
    color: #606266;
    background-color: #fff;
}

/* 分页 */
.pagination-wrapper {
    display: flex;
    justify-content: center;
    margin-top: 20px;
    padding: 20px 0;
}

.pagination {
    background: white;
    padding: 16px;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* 对话框 */
.detail-dialog .el-dialog__body,
.form-dialog .el-dialog__body,
.schedules-dialog .el-dialog__body,
.shares-dialog .el-dialog__body,
.logs-dialog .el-dialog__body {
    padding: 20px;
}

.robot-detail {
    padding: 20px 0;
}

.detail-section h3 {
    margin: 0 0 20px 0;
    color: #303133;
    font-size: 18px;
    font-weight: 600;
    border-bottom: 2px solid #e4e7ed;
    padding-bottom: 10px;
}

.detail-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
}

.detail-item {
    display: flex;
    align-items: center;
    padding: 12px;
    background: #f8f9fa;
    border-radius: 8px;
    border-left: 4px solid #667eea;
}

.detail-item label {
    font-weight: 600;
    color: #606266;
    margin-right: 8px;
    min-width: 100px;
}

.detail-item span {
    color: #303133;
}

.robot-form {
    padding: 20px 0;
}

.dialog-footer {
    text-align: right;
    padding-top: 20px;
    border-top: 1px solid #e4e7ed;
}

/* 响应式设计 */
@media (max-width: 768px) {
    .robots-container {
        padding: 10px;
    }

    .page-title {
        font-size: 24px;
    }

    .search-row {
        flex-direction: column;
        align-items: stretch;
    }

    .search-item {
        min-width: auto;
    }

    .search-actions {
        justify-content: center;
    }

    .action-buttons {
        justify-content: center;
    }

    .detail-grid {
        grid-template-columns: 1fr;
    }

    .more-dropdown {
        width: 100%;
    }

    .more-btn {
        width: 100%;
        justify-content: center;
    }
}
</style>
